import { EGE_H3_ZOOM_THRESHOLD } from '@const';
import { Box, Button, Modal, Paper, TextField, Typography } from '@mui/material';
import { useGetGeonamesQuery, useSaveViewMutation } from '@service';
import { sliceSelectors } from '@slice';
import { View } from '@types';
import { useFormik } from 'formik';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

export interface ShareButtonSaveModalProps {
  onClose: (cancelled: boolean) => void;
}

const ShareButtonSaveModal = ({ onClose }: ShareButtonSaveModalProps) => {
  const { delayedMapViewport, campaign } = useSelector(sliceSelectors.shareButtonSaveModalSelector);
  const state = useSelector(sliceSelectors.snapshot);

  const onCancel = useCallback(() => {
    onClose(true);
  }, [onClose]);

  const { currentData, isLoading } = useGetGeonamesQuery(
    {
      latitude: delayedMapViewport?.latitude as number,
      longitude: delayedMapViewport?.longitude as number
    },
    {
      skip: !delayedMapViewport || delayedMapViewport.zoom < EGE_H3_ZOOM_THRESHOLD
    }
  );
  let location = '';
  if (delayedMapViewport && currentData && currentData.length > 0) {
    if (delayedMapViewport.zoom < 9.5) {
      location = `near "${currentData[0].name}, ${currentData[0].country_code}"`;
    } else {
      location = `${currentData[0].name}, ${currentData[0].country_code}`;
    }
  }
  const [saveView] = useSaveViewMutation();
  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      location
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name) {
        errors.name = 'Required';
      }
      if (!values.location) {
        errors.location = 'Required';
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const view: View = {
        campaign_id: campaign?.id,
        name: values.name,
        description: values.description,
        location: values.location,
        state: JSON.stringify(state)
      };
      saveView(view)
        .unwrap()
        .then(() => {
          onClose(false);
          toast.success('View saved successfully');
        })
        .catch((error) => {
          console.error('Error saving view:', error);
        });
    }
  });

  return (
    <Modal open={true}>
      <form onSubmit={formik.handleSubmit}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 650,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            outline: 'none'
          }}
        >
          <Typography variant="h6">Save view</Typography>

          <TextField
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            name="name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Name"
            value={formik.values.name}
            placeholder="Interesting taco cabeza"
          />
          <TextField
            error={formik.touched.location && Boolean(formik.errors.location)}
            helperText={formik.touched.location && formik.errors.location}
            disabled={isLoading}
            name="location"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Location / Area"
            value={formik.values.location}
            placeholder="West of Paris"
          />
          <TextField
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            name="description"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            multiline={true}
            minRows={3}
            value={formik.values.description}
            label="Description (optional)"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 2
            }}
          >
            <Button type="button" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={!formik.isValid || !formik.values.name || !formik.values.location}>
              Save
            </Button>
          </Box>
        </Paper>
      </form>
    </Modal>
  );
};
export default memo(ShareButtonSaveModal);
